/**
 * 登入閘門：未登入顯示 `@tpte/auth-ui` 的 `SignInPage`（帳密／TOTP／passkey），
 * 持 `#/invite/<token>` 時顯示 `InviteRegisterPage`；登入後才掛 children。
 *
 * PIN 與公開註冊入口已關閉（2026-11-02）：建帳只走 admin 邀請連結。
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  InviteRegisterPage,
  SignInPage,
  passkeysSupported,
  type LoginResult,
  type SignInProvider,
} from '@tpte/auth-ui';
import { clearApiToken } from '@chat-rea/console-ui';
import { AUTH_SERVICE_URL, transport } from './config';
import { authStateFromUser, type SessionAuthState } from './session-auth';
import { setPinToken, setVmSession } from './session';
import { ApiTokenSettings } from './ApiTokenSettings';

export type AuthState = SessionAuthState;

interface AuthContextValue {
  readonly auth: AuthState;
  readonly signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthState | null {
  return useContext(AuthContext)?.auth ?? null;
}

export function useRelogin(): () => void {
  return useContext(AuthContext)?.signOut ?? (() => undefined);
}

type State =
  | { readonly kind: 'checking' }
  | { readonly kind: 'anonymous' }
  | { readonly kind: 'signedIn'; readonly auth: AuthState; readonly sessionId: string };

function inviteTokenFromHash(): string | null {
  const match = /^#\/invite\/([^/?#]+)/.exec(window.location.hash);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function devInjectedAuth(): AuthState | null {
  if (!import.meta.env.DEV) return null;
  const role = new URLSearchParams(window.location.search).get('devRole');
  if (role !== 'admin' && role !== 'chatter' && role !== 'engineer') return null;
  const roles =
    role === 'admin' ? (['admin'] as const) : role === 'engineer' ? (['engineer'] as const) : (['chatter'] as const);
  return {
    email: `dev-${role}@local`,
    roles,
    isAdmin: role === 'admin',
    isEngineer: role === 'admin' || role === 'engineer',
  };
}

function toAuth(result: LoginResult): AuthState {
  const user = result.user as {
    email: string;
    roles?: readonly string[] | null;
    role?: string | null;
  };
  return authStateFromUser({
    email: user.email,
    roles: user.roles,
    role: user.role,
  });
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ kind: 'checking' });
  const [inviteToken, setInviteToken] = useState<string | null>(() => inviteTokenFromHash());

  const passkeySupported = useMemo(() => passkeysSupported(), []);

  const providers = useMemo<readonly SignInProvider[]>(
    () => [
      { id: 'password', label: '密碼', kind: 'password', primary: true },
      { id: 'totp', label: '驗證器驗證碼', kind: 'totp' },
      {
        id: 'passkey',
        label: '用 Passkey 登入',
        kind: 'passkey',
        disabled: !passkeySupported,
        description: passkeySupported
          ? '用這台裝置的指紋／臉部／螢幕鎖登入；第一次仍要先用帳密登入並註冊。'
          : '這台裝置或瀏覽器不支援 passkey（需要安全連線與平台驗證器），請改用帳密登入。',
      },
    ],
    [passkeySupported],
  );

  const enter = useCallback((result: LoginResult) => {
    const auth = toAuth(result);
    setVmSession({ id: result.currentSessionId, roles: auth.roles });
    setState({ kind: 'signedIn', auth, sessionId: result.currentSessionId });
  }, []);

  useEffect(() => {
    const onHash = () => setInviteToken(inviteTokenFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const check = useCallback(
    (signal?: AbortSignal) => {
      const injected = devInjectedAuth();
      if (injected !== null) {
        setState({ kind: 'signedIn', auth: injected, sessionId: '' });
        return;
      }
      // 清除殘留 PIN token（正式站已關 PIN）
      setPinToken(null);
      transport
        .getSession(signal === undefined ? undefined : { signal })
        .then((result) => {
          if (signal?.aborted === true) return;
          if (result === null) {
            setVmSession(null);
            setState({ kind: 'anonymous' });
            return;
          }
          enter(result);
        })
        .catch(() => {
          if (signal?.aborted === true) return;
          setVmSession(null);
          setState({ kind: 'anonymous' });
        });
    },
    [enter],
  );

  useEffect(() => {
    const controller = new AbortController();
    check(controller.signal);
    return () => controller.abort();
  }, [check]);

  const signOut = useCallback(() => {
    void transport
      .logout()
      .catch(() => undefined)
      .finally(() => {
        setPinToken(null);
        setVmSession(null);
        clearApiToken();
        setState({ kind: 'anonymous' });
      });
  }, []);

  if (state.kind === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center font-mono text-xs uppercase tracking-[0.12em] text-text-secondary">
        檢查登入狀態…
      </div>
    );
  }

  if (state.kind === 'anonymous' && inviteToken) {
    return (
      <InviteRegisterPage
        token={inviteToken}
        transport={transport}
        authBaseUrl={AUTH_SERVICE_URL}
        title="建立 CHAT-REA 帳號"
        onRegistered={() => {
          window.location.hash = '#/inbox';
          setInviteToken(null);
          setState({ kind: 'anonymous' });
        }}
        onGoSignIn={() => {
          window.location.hash = '#/inbox';
          setInviteToken(null);
        }}
      />
    );
  }

  if (state.kind === 'anonymous') {
    // 不傳 brand：預設 realm 是 admin，有 brand 就開雙欄，左欄 bg-card 在淺色是整片白。
    return (
      <SignInPage
        transport={transport}
        mode="signin"
        title="登入 CHAT-REA 控制台"
        subtitle="請使用你的帳號登入；有啟用 TOTP 的帳號會要求第二階段驗證。已註冊 passkey 的裝置可以直接用 passkey。新帳號請使用管理員發給你的邀請連結。"
        footer={
          <span className="flex flex-col gap-1 font-mono text-[10px] leading-relaxed">
            <span className="tracking-[0.22em] text-text">CHAT-REA</span>
            <span className="text-text-tertiary">
              auth: {AUTH_SERVICE_URL.replace(/^https?:\/\//, '')}
            </span>
          </span>
        }
        providers={providers}
        defaultRemember
        onSignedIn={enter}
      />
    );
  }

  return (
    <AuthContext.Provider value={{ auth: state.auth, signOut }}>
      {children}
      <ApiTokenSettings />
    </AuthContext.Provider>
  );
}
